import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { brokerQuotes, type BrokerQuoteEstimateInput } from '@/shared/api/resources';
import { formatMoney, formatRate } from '@/shared/lib/format';
import { Card, CardBody } from '@/shared/ui/Card';

/** Espera a pessoa parar de digitar antes de pedir a estimativa. */
function useAtrasado<T>(valor: T, ms: number): T {
  const [atrasado, setAtrasado] = useState(valor);
  useEffect(() => {
    const espera = setTimeout(() => setAtrasado(valor), ms);
    return () => clearTimeout(espera);
  }, [valor, ms]);
  return atrasado;
}

const origemDaTaxa = {
  informada: 'taxa informada',
  sugerida: 'taxa sugerida pela faixa vigente',
  sem_taxa: 'sem faixa de taxa vigente',
} as const;

/**
 * O premio estimado no topo do assistente (NCT-075): o que o servidor ja calcula hoje,
 * importancia segurada vezes a taxa (`money.Money` e `ApplyRate`), sem gravar nada. As
 * regras de premio do OnPoint (premio minimo, IOF, parcelas e as outras) sao regra
 * pendente e aparecem pelo codigo.
 *
 * Sem modalidade ou importancia, o quadro mostra o que a cotacao gravada tem.
 */
export function PremiumEstimateBanner({
  entrada,
  gravado,
}: {
  entrada: BrokerQuoteEstimateInput | null;
  gravado?: { premio: string; taxa: string } | undefined;
}) {
  // A chave em texto: o objeto do pedido nasce a cada desenho, e o atraso precisa
  // comparar o conteudo.
  const chave = useAtrasado(entrada ? JSON.stringify(entrada) : '', 400);
  const estimativa = useQuery({
    queryKey: ['premium-estimate', chave],
    queryFn: () => brokerQuotes.estimate(JSON.parse(chave) as BrokerQuoteEstimateInput),
    enabled: chave !== '',
    placeholderData: keepPreviousData,
    retry: false,
  });

  const dados = entrada !== null ? estimativa.data : undefined;
  const pendentes = dados?.pending_rules.map((regra) => regra.code).join(', ');

  return (
    <Card>
      <CardBody className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-2xs text-ink-500">Premio estimado</p>
          <p className="text-lg font-semibold tabular-nums text-ink-900">
            {dados
              ? dados.premium
                ? formatMoney(dados.premium)
                : 'Sem premio'
              : (gravado?.premio ?? 'Informe a modalidade e a importancia segurada')}
          </p>
        </div>
        <div className="text-right text-2xs text-ink-600">
          {dados ? (
            <>
              <p>
                {dados.rate.applied_bps !== null
                  ? `${formatRate(dados.rate.applied_bps)} (${origemDaTaxa[dados.rate.source]})`
                  : origemDaTaxa[dados.rate.source]}
              </p>
              {dados.rate.outside_band ? (
                <p className="text-warning-700">A taxa esta fora da faixa vigente.</p>
              ) : null}
              <p>Importancia segurada x taxa. Nada e gravado.</p>
              {pendentes ? <p>Regras pendentes: {pendentes}.</p> : null}
            </>
          ) : gravado ? (
            <p>Taxa gravada: {gravado.taxa}</p>
          ) : null}
          {estimativa.isError && entrada !== null ? (
            <p className="text-danger-700">{estimativa.error.message}</p>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}
